import { useCallback, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronRight, Upload } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getTaxLabelKey,
  getAuthorityLabelKey,
  formatPeriodForDisplay,
} from "@/lib/fiscalAgendaDisplay";
import { SolicitudMobileCard } from "@/pages/solicitudes/SolicitudMobileCard";
import {
  getTenantAccountantRequests,
  type TenantAccountantRequest,
} from "@/services/fiscalService";

export type SolicitudesTab = "pendientes" | "completados";

const TAB_PARAM = "tab";

function tabFromParam(param: string | null): SolicitudesTab {
  if (param === "completados" || param === "pendientes") return param;
  return "pendientes";
}

export function SolicitudesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromParam(searchParams.get(TAB_PARAM));

  const statuses: Array<"pending" | "in_progress" | "completed"> =
    tab === "completados" ? ["completed"] : ["pending", "in_progress"];

  const { user } = useAuth();

  // The endpoint below calls assertPaidPlan (fiscal.controller.ts:257) and 403s
  // for a free tenant. Gating the ROUTE is not enough on its own: PlanGateOverlay
  // renders its children inside a blurred aria-hidden container, so the page
  // still mounts and this query still fires — the 403 just becomes invisible.
  // With refetchOnWindowFocus that repeats on every tab focus, which is how one
  // tenant produced 124 failed requests over three days.
  const { data: planTenant, isFetched: planFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const hasPaidPlan = planFetched && isProOrUltraPlan(planTenant?.planId);

  const {
    data: requests = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tenant-accountant-requests", tab],
    queryFn: () => getTenantAccountantRequests(statuses),
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    enabled: hasPaidPlan,
  });

  const sortedRequests = useMemo(() => {
    const list = [...requests];
    if (tab === "completados") {
      return list.sort(
        (a, b) =>
          new Date(b.completedAt ?? 0).getTime() -
          new Date(a.completedAt ?? 0).getTime()
      );
    }
    return list.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [requests, tab]);

  const inProgressRowActions = useCallback(
    (req: TenantAccountantRequest): TableAction[] => [
      {
        label: t("solicitudes.uploadDocument"),
        icon: Upload,
        onClick: () => navigate(`/requests/${req.id}`),
      },
    ],
    [navigate, t]
  );

  if (isError) {
    toast.error(t("solicitudes.loadError"));
  }

  const emptyTitle =
    tab === "completados"
      ? t("solicitudes.emptyTitleCompleted")
      : t("solicitudes.emptyTitle");
  const emptyMessage =
    tab === "completados"
      ? t("solicitudes.emptyMessageCompleted")
      : t("solicitudes.emptyMessage");

  return (
    <div className="fx-content">
      <PageHeader
        title={t("solicitudes.title")}
        description={t("solicitudes.subtitle")}
      />

      <div className="mt-4">
        <Tabs
          value={tab}
          onValueChange={(v) => {
            const newTab = v === "completados" ? "completados" : "pendientes";
            setSearchParams({ [TAB_PARAM]: newTab });
          }}
        >
          <TabsList className="mb-2">
            <TabsTrigger value="pendientes">
              {t("solicitudes.tabPendientes")}
            </TabsTrigger>
            <TabsTrigger value="completados">
              {t("solicitudes.tabCompletados")}
            </TabsTrigger>
          </TabsList>
          <TableCard>
          {isLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <LoadingLogo size="lg" />
            </div>
          ) : sortedRequests.length === 0 ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <EmptyState
                title={emptyTitle}
                message={emptyMessage}
              />
            </div>
          ) : (
            <>
              <div className="space-y-3 py-2 md:hidden">
                {sortedRequests.map((req) => (
                  <SolicitudMobileCard
                    key={req.id}
                    req={req}
                    tab={tab}
                    inProgressRowActions={inProgressRowActions}
                  />
                ))}
              </div>
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("solicitudes.columnRequest")}</TableHead>
                    <TableHead>{t("solicitudes.columnType")}</TableHead>
                    <TableHead>{t("solicitudes.columnSubmitted")}</TableHead>
                    <TableHead>{t("solicitudes.columnStatus")}</TableHead>
                    {tab === "completados" && (
                      <TableHead>{t("solicitudes.columnCompleted")}</TableHead>
                    )}
                    <TableHead className="w-[80px]" aria-hidden />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRequests.map((req) => {
                    const ob = req.fiscalObligation;
                    const inProgress = req.status === "in_progress";
                    const completed = req.status === "completed";
                    return (
                      <TableRow
                        key={req.id}
                        className="cursor-pointer transition-colors hover:bg-muted/50"
                        onClick={() => navigate(`/requests/${req.id}`)}
                      >
                        <TableCell className="font-medium">
                          {t(getTaxLabelKey(ob.taxType))} ({t(getAuthorityLabelKey(ob.authority))}) – {formatPeriodForDisplay(ob.period, i18n.language)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {t("solicitudes.typePaymentSlip")}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDisplayDate(req.createdAt, {
                            locale: i18n.language,
                          })}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            variant={
                              completed ? "success" : inProgress ? "info" : "warning"
                            }
                          >
                            {completed
                              ? t("solicitudes.statusCompleted")
                              : inProgress
                                ? t("solicitudes.statusInProgress")
                                : t("solicitudes.statusPending")}
                          </StatusBadge>
                        </TableCell>
                        {tab === "completados" && (
                          <TableCell className="text-muted-foreground">
                            {req.completedAt
                              ? formatDisplayDate(req.completedAt, {
                                  locale: i18n.language,
                                })
                              : "—"}
                          </TableCell>
                        )}
                        <TableCell
                          className="text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {inProgress ? (
                            <TableActionsMenu
                              actions={inProgressRowActions(req)}
                              onTriggerClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
          </TableCard>
        </Tabs>
      </div>
    </div>
  );
}
